import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { Clock, Mail, Phone, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/components/ui/sonner";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { SEO } from "@/components/SEO";
import { images, services } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialForm = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  service: "General consultation",
  preferredContact: "Email",
  preferredDate: "",
  preferredTime: "Morning",
  message: "",
  company: "",
};

const Contact = () => {
  const [searchParams] = useSearchParams();
  const requestedService = searchParams.get("service");
  const [form, setForm] = useState({ ...initialForm, service: requestedService || initialForm.service });
  const [submitting, setSubmitting] = useState(false);

  const updateField = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
  };

  const submitForm = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/consultation-requests`, form);
      toast.success("Your consultation request has been sent. Alicia’s office will reach out soon.");
      setForm({ ...initialForm, service: form.service });
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(detail || "We could not send your request. Please call 469-682-7618.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <SEO
        title="Contact | Bello Joy Consulting"
        description="Schedule a consultation with Alicia Joy at Bello Joy Consulting. Call 469-682-7618 or email info@bellojoyconsulting.com."
      />
      <PageHero
        testId="contact-page-hero"
        kicker="Contact"
        title="Begin a conversation"
        accent="with Alicia."
        description="You are welcome here. Share what is on your heart, ask a question, or request a consultation—Alicia’s office will respond with warmth and care."
      />

      <section data-testid="contact-main-section" className="px-6 pb-24 md:pb-32">
        <div className="mx-auto grid max-w-7xl gap-10 lg:grid-cols-[0.85fr_1.15fr]">
          <Reveal className="space-y-6" data-testid="contact-info-panel">
            <div className="image-frame overflow-hidden rounded-[2rem] shadow-soft">
              <img src={images.contact} alt="A welcoming professional preparing for a consultation" className="aspect-[4/3] w-full object-cover" loading="lazy" />
            </div>
            <div className="rounded-[2rem] border border-border bg-card p-8 shadow-soft">
              <p className="mb-5 text-xs font-semibold uppercase tracking-[0.28em] text-secondary">Contact information</p>
              <a href="tel:+14696827618" data-testid="contact-phone-link" className="mb-5 flex items-center gap-4 text-primary transition-colors hover:text-secondary">
                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-muted"><Phone size={19} /></span>
                <span className="font-semibold">469-682-7618</span>
              </a>
              <a href="mailto:info@bellojoyconsulting.com" data-testid="contact-email-link" className="mb-5 flex items-center gap-4 break-all text-primary transition-colors hover:text-secondary">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-muted"><Mail size={19} /></span>
                <span className="font-semibold">info@bellojoyconsulting.com</span>
              </a>
              <div data-testid="contact-office-hours" className="flex items-start gap-4 text-primary">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-muted"><Clock size={19} /></span>
                <div>
                  <p className="font-semibold">Office hours</p>
                  <p className="mt-1 text-sm leading-relaxed text-muted-foreground">Monday–Friday, 9:00 AM–5:00 PM CT. Evening appointments by request.</p>
                </div>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.1} className="rounded-[2rem] border border-border bg-card p-8 shadow-soft md:p-10" data-testid="consultation-form-card">
            <p className="mb-4 text-xs font-semibold uppercase tracking-[0.28em] text-secondary">Consultation request</p>
            <h2 className="text-3xl tracking-tight text-primary md:text-4xl">Tell Alicia what you would like to protect.</h2>
            <p className="mt-4 text-base leading-relaxed text-muted-foreground">
              This simple form sends your request directly to Bello Joy Consulting. There is no pressure—just a thoughtful next step.
            </p>

            <form onSubmit={submitForm} className="mt-9 grid gap-7" data-testid="consultation-request-form">
              <input type="text" name="company" value={form.company} onChange={updateField} tabIndex="-1" autoComplete="off" className="hidden" aria-hidden="true" />
              <div className="grid gap-7 md:grid-cols-2">
                <label className="block" htmlFor="firstName">
                  <span className="mb-2 block text-sm font-medium text-primary">First name</span>
                  <Input id="firstName" name="firstName" data-testid="first-name-input" value={form.firstName} onChange={updateField} required className="form-field h-12" />
                </label>
                <label className="block" htmlFor="lastName">
                  <span className="mb-2 block text-sm font-medium text-primary">Last name</span>
                  <Input id="lastName" name="lastName" data-testid="last-name-input" value={form.lastName} onChange={updateField} required className="form-field h-12" />
                </label>
              </div>
              <div className="grid gap-7 md:grid-cols-2">
                <label className="block" htmlFor="email">
                  <span className="mb-2 block text-sm font-medium text-primary">Email</span>
                  <Input id="email" name="email" type="email" data-testid="email-input" value={form.email} onChange={updateField} required className="form-field h-12" />
                </label>
                <label className="block" htmlFor="phone">
                  <span className="mb-2 block text-sm font-medium text-primary">Phone</span>
                  <Input id="phone" name="phone" type="tel" data-testid="phone-input" value={form.phone} onChange={updateField} className="form-field h-12" />
                </label>
              </div>
              <div className="grid gap-7 md:grid-cols-2">
                <label className="block" htmlFor="service">
                  <span className="mb-2 block text-sm font-medium text-primary">What can Alicia help with?</span>
                  <select id="service" name="service" data-testid="service-select" value={form.service} onChange={updateField} className="form-field h-12 w-full border-b bg-transparent text-sm text-primary focus-visible:outline-none">
                    <option>General consultation</option>
                    {services.map((service) => (
                      <option key={service.slug}>{service.title}</option>
                    ))}
                  </select>
                </label>
                <label className="block" htmlFor="preferredContact">
                  <span className="mb-2 block text-sm font-medium text-primary">Preferred contact</span>
                  <select id="preferredContact" name="preferredContact" data-testid="preferred-contact-select" value={form.preferredContact} onChange={updateField} className="form-field h-12 w-full border-b bg-transparent text-sm text-primary focus-visible:outline-none">
                    <option>Email</option>
                    <option>Phone</option>
                    <option>Either is fine</option>
                  </select>
                </label>
              </div>
              <div className="rounded-3xl border border-secondary/30 bg-secondary/10 p-6">
                <p className="mb-4 text-sm font-semibold uppercase tracking-[0.18em] text-primary">Choose a preferred consultation time</p>
                <div className="grid gap-7 md:grid-cols-2">
                  <label className="block" htmlFor="preferredDate">
                    <span className="mb-2 block text-sm font-medium text-primary">Preferred date</span>
                    <Input id="preferredDate" name="preferredDate" type="date" min={new Date().toISOString().split("T")[0]} data-testid="preferred-date-input" value={form.preferredDate} onChange={updateField} className="form-field h-12" />
                  </label>
                  <label className="block" htmlFor="preferredTime">
                    <span className="mb-2 block text-sm font-medium text-primary">Preferred time</span>
                    <select id="preferredTime" name="preferredTime" data-testid="preferred-time-select" value={form.preferredTime} onChange={updateField} className="form-field h-12 w-full border-b bg-transparent text-sm text-primary focus-visible:outline-none">
                      <option>Morning</option>
                      <option>Afternoon</option>
                      <option>Early evening</option>
                    </select>
                  </label>
                </div>
                <p className="mt-4 text-xs leading-relaxed text-muted-foreground">Alicia’s office will confirm the actual appointment time after reviewing your request.</p>
              </div>
              <label className="block" htmlFor="message">
                <span className="mb-2 block text-sm font-medium text-primary">Your message</span>
                <Textarea id="message" name="message" data-testid="message-textarea" value={form.message} onChange={updateField} required minLength={10} className="form-field min-h-36 resize-y" placeholder="Share a little about your family, business, goals, or questions." />
              </label>
              <Button type="submit" disabled={submitting} data-testid="consultation-submit-button" className="h-13 rounded-full bg-primary px-8 py-6 text-primary-foreground transition-[transform,background-color] duration-300 hover:-translate-y-1 hover:bg-primary/90">
                {submitting ? "Sending with care…" : "Send Consultation Request"} <Send size={17} />
              </Button>
            </form>
          </Reveal>
        </div>
      </section>
    </>
  );
};

export default Contact;
